/**
 * Appearance preference: stored value vs the class that actually paints.
 *
 * `khorium-theme` holds the user's choice (`light` / `dark` / `academic` /
 * `system`). `system` is not a class — it resolves through
 * `prefers-color-scheme` to `.dark` or the light `:root`. A missing or
 * unknown value stays `light`, so a first visit never auto-darkens.
 *
 * `main.tsx` applies the stored choice before first paint and subscribes
 * so a System user tracks an OS flip. `ThemePicker` is the only writer.
 */

export const THEME_STORAGE_KEY = 'khorium-theme';

const THEME_PREFERENCES = ['system', 'light', 'dark', 'academic'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** What actually lands on `<html>` — `system` is never a class. */
export type ResolvedTheme = 'light' | 'dark' | 'academic';

const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

export function isThemePreference(
  value: string | null | undefined,
): value is ThemePreference {
  return THEME_PREFERENCES.some((theme) => theme === value);
}

export function readThemePreference(): ThemePreference {
  if (typeof localStorage === 'undefined') return DEFAULT_THEME_PREFERENCE;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
}

/** OS dark when the query exists and matches; otherwise light. */
export function prefersSystemDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

export function resolveTheme(
  preference: ThemePreference = readThemePreference(),
): ResolvedTheme {
  if (preference === 'system') {
    return prefersSystemDark() ? 'dark' : 'light';
  }
  return preference;
}

export function isResolvedThemeDark(preference?: ThemePreference): boolean {
  return resolveTheme(preference) === 'dark';
}

/** Inject Google Fonts stylesheet for Academic theme (idempotent). */
function loadAcademicFonts() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('academic-fonts')) return;
  const link = document.createElement('link');
  link.id = 'academic-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400..800;1,400..800&family=JetBrains+Mono:wght@400;500&display=swap';
  document.head.appendChild(link);
}

export function applyTheme(
  preference: ThemePreference = readThemePreference(),
): ResolvedTheme {
  const resolved = resolveTheme(preference);
  if (typeof document === 'undefined') return resolved;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  // For components that read `data-theme` rather than our `.dark` class — the
  // vendored tool-ui code block does, and otherwise asks the OS.
  document.documentElement.dataset.theme =
    resolved === 'dark' ? 'dark' : 'light';
  document.documentElement.classList.toggle(
    'theme-academic',
    resolved === 'academic',
  );
  if (resolved === 'academic') loadAcademicFonts();
  return resolved;
}

export function setThemePreference(preference: ThemePreference): ResolvedTheme {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  }
  return applyTheme(preference);
}

/**
 * Re-apply when the OS appearance flips, but only while the stored
 * preference is `system`. Safe to call once at boot; returns an unsubscribe
 * for tests. No-ops where `matchMedia` is missing.
 */
export function subscribeSystemTheme(): () => void {
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  if (!media) return () => {};

  const onChange = () => {
    if (readThemePreference() === 'system') applyTheme('system');
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
